import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { UnitEditBasic } from "@shared/models/analytics/unit"
import type { Course } from "@shared/models/course"
import type { PatchModel } from "@shared/models/patch"
import type { Planbook } from "@shared/models/planbook"
import type { RotationCalendar } from "@shared/models/rotation-calendar"
import Patch from "@shared/models/patch-updater"
import {
  consolidateCourseDatesOff,
  constructUnitsAndDates,
  moveEndOfUnit,
  moveStartOfUnit,
} from "cc-frontend/lib/unit-utils"
import { find, get } from "lodash-es"
import type { Narrative } from "@shared/actions/create-narrative"

interface Payload {
  unitId: string
  amount: number
  course: Course
  startOrEndOfUnit: "START" | "END"
  planbook: Planbook
  rotationCalendar: RotationCalendar
  newDate: string
}

type MovingCalendarPatch = PatchModel & {
  unitIdsOnTheMove?: string[]
  cardStackIdsOnTheMove?: string[]
}

export default {
  name: "RESIZE_UNIT",

  params: {
    unitId: "string",
    amount: "number",
    course: "object",
    startOrEndOfUnit: "string",
    planbook: "object",
    rotationCalendar: "object",
  },

  /**
    @param {{
      unit: Object,
      amount: Number,
      newDate?: String,
      courseId: String,
      courseDates: Object[],
      startOrEndOfUnit: "START" | "END",
      consolidatedCourseDatesOff: Object[],
      schoolDays: Number[]
    }} payload [description]
    @return {Array} Patches
   */
  patches(payload: Payload) {
    let schoolDays = payload.rotationCalendar.attributes.schoolDays
    let unit = find(payload.course.attributes.calendar.units, {
      id: payload.unitId,
    })

    let consolidatedCourseDatesOff = consolidateCourseDatesOff(
      payload.course,
      payload.planbook,
      payload.rotationCalendar
    )

    let newCourseDates
    if (unit && payload.startOrEndOfUnit === "END") {
      newCourseDates = moveEndOfUnit(
        payload.amount,
        payload.newDate,
        unit,
        payload.course.attributes.calendar.dates,
        payload.course.attributes.calendar.units,
        consolidatedCourseDatesOff,
        schoolDays
      )
    }
    if (unit && payload.startOrEndOfUnit === "START") {
      newCourseDates = moveStartOfUnit(
        payload.amount,
        payload.newDate,
        unit,
        payload.course.attributes.calendar.dates,
        payload.course.attributes.calendar.units,
        consolidatedCourseDatesOff,
        schoolDays
      )
    }

    let ret
    try {
      ret = constructUnitsAndDates(newCourseDates, consolidatedCourseDatesOff, schoolDays)
    } catch (e) {
      console.error(e)
      return []
    }

    let { courseDates, units } = ret

    let unitIdsOnTheMove = get(find(units, { id: payload.unitId }), "descendantIds") || []
    unitIdsOnTheMove = unitIdsOnTheMove.concat([payload.unitId])
    let cardStackIdsOnTheMove: string[] = []

    let patch: MovingCalendarPatch = Patch.create("course", payload.course.id)
      .set(`attributes.calendar.dates`, courseDates)
      .set(`attributes.calendar.units`, units)
      .inc(`attributes.calendar.version`, 1).patch

    // @ts-ignore
    patch.unitIdsOnTheMove = unitIdsOnTheMove
    // @ts-ignore
    patch.cardStackIdsOnTheMove = cardStackIdsOnTheMove

    return [patch]
  },

  undoPatches(payload: Payload) {
    let patch = Patch.create("course", payload.course.id)
      .set(
        "attributes.calendar.dates",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.dates))
      )
      .set(
        "attributes.calendar.units",
        JSON.parse(JSON.stringify(payload.course.attributes.calendar.units))
      ).patch
    return [patch]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let course = payload.course
    let models = {
      course: course,
      planbook: findFn("planbook", payload.planbook.id),
      unit: findFn("card-stack-summary", payload.unitId),
    }

    let unitTitle = models.unit?.attributes.title
    let amount = Math.abs(payload.amount)
    let direction = payload.amount > 0 ? "longer" : "shorter"
    let unit = find(course.attributes.calendar.units, { id: payload.unitId })
    let dayOrDays = payload.amount === 1 ? "day" : "days"
    let newEvent: UnitEditBasic = {
      ...createBlankEvent(),
      root_id: models.planbook.id,
      root_type: "planbook",
      date_plan_scheduled_for: null,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook.id),

      primary_object_type: "unit",
      primary_object_id: payload.unitId,
      primary_object_specifier: null,
      primary_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],

      adverbial_verb: "resizing",

      adverbial_object_type: "unit",
      adverbial_object_id: payload.unitId,
      adverbial_object_specifier: null,
      adverbial_object_id_and_ancestor_ids: [payload.planbook.id, payload.course.id],
    }

    return {
      title: "Resized Unit",
      titleForAnalytics: "Unit - Resize",
      verbForStream: "edited",
      description: `Resized "${unitTitle}" to make it ${direction} by ${amount} ${dayOrDays}`,
      context: {
        unitTitle: models.unit?.attributes.title,
        courseTitle: models.course?.attributes.title,
        planbookTitle: models.planbook.attributes.title,
        unitId: payload.unitId,
        courseId: payload.course.id,
        planbookId: models.planbook.id,
      },
      activity: {
        actor: `user-${userId}`,
        verb: "edited",
        object: `course-${models.course.id}`,
        to: [
          `course-edits:course-${models.course.id}`,
          `planbook-edits:planbook-${models.planbook.id}`,
        ],
      },
      newAnalyticsEvents: [newEvent],
    }
  },
}
